
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PractitionersHeader from "@/components/practitioners/PractitionersHeader";
import PractitionersContent from "@/components/practitioners/PractitionersContent";
import PractitionersLoading from "@/components/practitioners/PractitionersLoading";
import { usePractitionersLocation } from "@/hooks/usePractitionersLocation";
import { usePractitionersFilters } from "@/hooks/usePractitionersFilters";
import { Practitioner } from "@/types/practitioner";

// Export types for backward compatibility
export type { Practitioner, PractitionerService } from "@/types/practitioner";

// Helper function to map healing stage number to category name
const mapStageToCategory = (stage: number | null | undefined): string | undefined => {
  if (stage === null || stage === undefined) return undefined;
  const mapping: Record<number, string> = {
    0: 'crisis_intervention',
    1: 'harmonize_mind_body',
    2: 'analysis_investigation',
    3: 'transpersonal_dive',
    4: 'actualization_integration'
  };
  return mapping[stage];
};

export default function Practitioners() {
  const location = useLocation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showSidebar, setShowSidebar] = useState(true);
  const [isPageLoading, setIsPageLoading] = useState(true);

  // Extract state from ServiceSelection navigation with sessionStorage fallback
  const navState = (location.state as {
    healingStage?: number;
    effectiveDomain?: string;
    subcategory?: string;
  } | null) || null;

  // Fallback to sessionStorage if location.state is empty
  const persistedRaw = sessionStorage.getItem('booking_filters');
  const persisted = persistedRaw ? JSON.parse(persistedRaw) : null;

  const healingStage = navState?.healingStage ?? persisted?.healingStage ?? null;
  const effectiveDomain = navState?.effectiveDomain ?? persisted?.effectiveDomain ?? undefined;
  const subcategory = navState?.subcategory ?? persisted?.subcategory ?? undefined;

  // Map to filter format for initial state
  const initialFilters = {
    serviceCategory: mapStageToCategory(healingStage),
    effectiveDomain: effectiveDomain,
    serviceSubcategory: subcategory
  };

  console.log('📍 Practitioners initialized with filters:', initialFilters);
  
  // Check if user came from booking choice or service selection
  const choiceMade = sessionStorage.getItem('booking_choice_made') === 'true';
  
  // Enhanced routing guard: allow if stage is selected (even if domain/subcategory are all_*)
  const hasInitialStage = !!initialFilters.serviceCategory && initialFilters.serviceCategory !== 'all_types';
  const hasNavFilters = !!navState || !!persistedRaw || hasInitialStage;
  
  const {
    userLocation,
    userAddress,
    locationError,
    isGettingLocation,
    isLoadingAddress,
    city,
    state,
    country,
    timezoneDisplay,
    isApproximateLocation,
    requestLocationAccess,
    setManualLocation,
    isGeolocationPolicyBlocked
  } = usePractitionersLocation();

  const {
    filters,
    setFilters,
    sortBy,
    setSortBy,
    searchQuery,
    setSearchQuery,
    debouncedSearchQuery,
    timeSlots,
    handleCategoryChange,
    isDateDisabled
  } = usePractitionersFilters(initialFilters);

  // Clean up sessionStorage after reading filters
  useEffect(() => {
    console.log('📊 Practitioners page loaded with:');
    console.log('  - navState:', navState);
    console.log('  - persistedRaw:', persistedRaw);
    console.log('  - choiceMade:', choiceMade);
    console.log('  - hasNavFilters:', hasNavFilters);
    console.log('  - initialFilters:', initialFilters);

    if (persistedRaw) {
      console.log('🗑️ Clearing booking_filters from sessionStorage');
      sessionStorage.removeItem('booking_filters');
    }
  }, []);

  // Smart route guard: redirect to booking-choice if user directly accesses without any context
  // TEMPORARILY DISABLED FOR DEBUGGING
  // useEffect(() => {
  //   if (!choiceMade && !hasNavFilters) {
  //     console.log('⚠️ No booking choice or filters found, redirecting to booking-choice');
  //     navigate('/booking-choice', { replace: true });
  //   }
  // }, [choiceMade, hasNavFilters, navigate]);

  // Initial location request
  useEffect(() => {
    const timer = setTimeout(() => {
      if (navigator.geolocation) {
        requestLocationAccess();
      }
      setIsPageLoading(false);
    }, 200);

    return () => clearTimeout(timer);
  }, [requestLocationAccess]);

  // Show initial loading state
  if (isPageLoading) {
    return <PractitionersLoading />;
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow pt-16">
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 min-h-full">
          <div className="container mx-auto px-4 py-8">
            <PractitionersHeader />
            
            <PractitionersContent
              showSidebar={showSidebar}
              setShowSidebar={setShowSidebar}
              filters={filters}
              setFilters={setFilters}
              sortBy={sortBy}
              setSortBy={setSortBy}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              debouncedSearchQuery={debouncedSearchQuery}
              userLocation={userLocation}
              userAddress={userAddress}
              locationError={locationError}
              isGettingLocation={isGettingLocation}
              isLoadingAddress={isLoadingAddress}
              requestLocationAccess={requestLocationAccess}
              timeSlots={timeSlots}
              isDateDisabled={isDateDisabled}
              handleCategoryChange={handleCategoryChange}
              city={city}
              state={state}
              country={country}
              timezoneDisplay={timezoneDisplay}
              isApproximateLocation={isApproximateLocation}
            />
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
